import {
  clearMediaState,
  createMedia,
  fetchMediaById,
} from "app/actions/media-library-data";
import { MediaModel } from "app/models";
import { useActions } from "app/utils/hooks";
import upload from "app/utils/uploader";
import { useRef, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";

interface Props {
  onClose: () => void;
  onSelectMedia: (media: MediaModel[]) => void;
  hasMultiSelect: boolean;
  alreadySelected?: MediaModel[];
}

const useFileUpload = ({
  onClose,
  onSelectMedia,
  hasMultiSelect = false,
  alreadySelected = [],
}: Props) => {
  const mediaActions = useActions({
    createMedia,
    clearMediaState,
    fetchMediaById,
  });
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [selectedTab, setSelectedTab] = useState<tabName>("upload");
  const [selectedList, setSelectedList] = useState<MediaModel[]>(
    alreadySelected || [],
  );
  type tabName = "upload" | "library";

  const uploadRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (uploadRef.current) {
        uploadRef.current.abort();
        uploadRef.current = null;
      }
    };
  }, []);

  const modalHandler = () => {
    if (uploadRef.current) {
      uploadRef.current.abort();
      uploadRef.current = null;
    }

    if (isMounted.current) {
      setIsLoading(false);
      setProgress(0);
      setSelectedTab("upload");
      setUploadError(null);
    }

    onClose();
  };

  const mediaSelectHandler = (data: MediaModel) => {
    setSelectedList((prev) => {
      if (hasMultiSelect) {
        const exists = prev?.some((item) => item.id === data.id);
        if (exists) {
          return prev?.filter((item) => item.id !== data.id);
        } else {
          return [...prev, data];
        }
      } else {
        return [data];
      }
    });
  };

  const onSelect = async () => {
    onSelectMedia(selectedList);
    modalHandler();
  };

  const onTabSelect = (tabName: tabName) => {
    setSelectedTab(tabName);
    if (!hasMultiSelect) {
      setSelectedList([]);
    }
  };

  const extractProgress = (progressEvent: any): number => {
    let fileProgress = 0;

    if (typeof progressEvent === "number") {
      fileProgress = progressEvent;
    } else if (progressEvent && typeof progressEvent === "object") {
      const event = progressEvent as any;
      if ("loaded" in event && "total" in event && event.total > 0) {
        fileProgress = (event.loaded / event.total) * 100;
      } else if ("percent" in event && typeof event.percent === "number") {
        fileProgress = event.percent;
      } else if ("progress" in event && typeof event.progress === "number") {
        fileProgress = event.progress;
      }
    }

    return fileProgress;
  };

  const onDrop = async (acceptedFiles: File[]) => {
    setUploadError(null);
    if (acceptedFiles.length > 0) {
      setIsLoading(true);
      dispatch(clearMediaState());
      setProgress(0);
      setUploadError(null);
      uploadRef.current = null;

      let completed = 0;
      const total = acceptedFiles.length;
      const parsedResults: MediaModel[] = [];

      for (const file of acceptedFiles) {
        if (uploadRef.current && uploadRef.current.isAborted) {
          console.log("Upload cancelled - stopping file processing");
          break;
        }

        try {
          const abortableUpload = upload(file, file.name, file.type, undefined);
          uploadRef.current = abortableUpload;

          abortableUpload.onProgress = (progressEvent) => {
            const fileProgress = extractProgress(progressEvent);

            if (total === 1) {
              setProgress(Math.floor(fileProgress));
            } else {
              const overallProgress =
                ((completed + fileProgress / 100) / total) * 100;
              setProgress(Math.floor(overallProgress));
            }
          };

          const uploadResult = await abortableUpload.promise;

          const { id, filename } = uploadResult as {
            id: string;
            filename: string;
          };
          try {
            const res = await mediaActions.createMedia({ id, filename });
            if (res && onSelectMedia) {
              const parsedRes = JSON.parse(res);
              if (hasMultiSelect) {
                parsedResults.push(parsedRes);
              } else {
                parsedResults.splice(0, parsedResults?.length);
                parsedResults.push(parsedRes);
              }
            }
          } catch (error) {
            setUploadError(error);
          }
          completed += 1;
          setProgress(Math.floor((completed / total) * 100));
          if (completed === total) {
            if (parsedResults.length > 0 && onSelectMedia) {
              if (hasMultiSelect) {
                onSelectMedia([...selectedList, ...parsedResults]);
              } else {
                onSelectMedia(parsedResults);
              }
            }
            setIsLoading(false);
            setProgress(100);
            modalHandler();
          }
        } catch (error) {
          if (
            error?.message === "UPLOAD_CANCELLED" ||
            uploadRef.current?.isAborted
          ) {
            console.log("Upload cancelled - exiting loop");
            break;
          }

          console.error("Error during file upload:", error);
          setUploadError(error);
          completed += 1;
          if (completed === total) {
            if (parsedResults.length > 0 && onSelectMedia) {
              onSelectMedia(parsedResults);
            }
            setIsLoading(false);
            setProgress(100);
            modalHandler();
          }
        }
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: hasMultiSelect ? undefined : 1,
    multiple: hasMultiSelect,
    noClick: true,
    accept: {
      "audio/*": [".m4a", ".mp3", ".wav", ".wma", ".aif", ".ogg"],
      "document/doc": [
        ".doc",
        ".docx",
        ".pdf",
        ".xls",
        ".xlsx",
        ".ppt",
        ".pptx",
      ],
      "image/*": [".gif", ".jpeg", ".jpg", ".png", ".webp", ".heic"],
      "video/*": [
        ".mov",
        ".avi",
        ".mp4",
        ".m4v",
        ".mkv",
        ".wmv",
        ".mpg",
        ".mpeg",
        ".webm",
        ".ogv",
        ".3gp",
        ".3gp2",
        ".3g2",
        ".3gpp",
        ".3gpp2",
      ],
    },
  });

  return {
    isLoading,
    uploadError,
    progress,
    selectedTab,
    onTabSelect,
    getRootProps,
    getInputProps,
    isDragActive,
    modalHandler,
    mediaSelectHandler,
    selectedList,
    onSelect,
  };
};

export default useFileUpload;
